//Built-in libraries

//Third party libraries
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useParams } from 'react-router';
import { signApplicationPublicAsset } from '@/api/Rover/ApplicationForm';
//Custom components
import Button from '@/components/UI/Button';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import HookMaskedInput from '@/components/UI/HookForm/Form/MaskedInput';
import { HookRadioGroup } from '@/components/UI/HookForm/Form/RadioGroup';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { type UploadFnReturn, useFileUpload } from '@/hooks/useFileUpload';

//STP Libraries

//Assets and Miscellaneous

import { TrashIcon } from '@heroicons/react/24/outline';
import type { ISignApplicationPublicAssetPayload } from '@/api/Rover/ApplicationForm/types';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import type { SignedApplicationAssetResponse } from '@/types/Shared';
import { compareTwoArrays, formatNumber } from '@/utils';

//Component Begin

export default function ProjectFinancial() {
  const params = useParams<string>();
  const [uploadError, setUploadError] = useState<string>('');
  const { upload } = useFileUpload({
    signAsset: signApplicationPublicAsset,
  });

  const { setCurrentStep, updateData, data: formContextData } = useApplicationFormContext();

  const {
    control,
    trigger,
    getValues,
    formState: { errors, isValid },
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'projectFinance.sources',
  });
  const sourcesFormValues = useWatch({ name: 'projectFinance.sources' });
  const usesFormValues = useWatch({ name: 'projectFinance.uses' });

  // Total Uses
  const totalUses = () => {
    let total = 0;
    total += Number(usesFormValues.acquisition) ?? 0;
    total += Number(usesFormValues.hardCost) ?? 0;
    total += Number(usesFormValues.softCost) ?? 0;
    total += Number(usesFormValues.developerFeeAmount) ?? 0;
    total += Number(usesFormValues.financeCost) ?? 0;
    return total;
  };
  const formattedTotalUses = formatNumber(totalUses());

  // Total Sources
  const totalSources = ((sourcesFormValues as []) || []).reduce<number>(
    (partialSum = 0, a: { name: string; amount: number }) => partialSum + (a?.amount || 0),
    0
  );
  const budgetTotal = formatNumber(sourcesFormValues ? totalSources : '0');

  const addNewSource = async () => {
    const sourcesValidation = await trigger('projectFinance.sources');
    if (sourcesValidation || getValues('projectFinance.sources').length === 0) {
      append({ name: '', amount: 0 });
    }
  };

  const uploadFile = useMutation((payload: { signAssetPayload: ISignApplicationPublicAssetPayload[]; files: File[] }) => upload(payload), {
    onSuccess: ({ sign, files }: UploadFnReturn) => {
      let signRes: SignedApplicationAssetResponse[] = [];
      if (Array.isArray(sign)) {
        signRes = [...sign];
      } else {
        signRes = [{ ...sign }];
      }

      updateData({
        projectFinance: {
          ...getValues('projectFinance'),
          additionalDocs: signRes.map((sig, index) => ({
            name: files[index].name,
            path: sig.path,
            key: sig.key,
            type: files[index].type,
            field: 'financeDocs',
            description: 'Additional Docs',
          })),
        },
      });
      setCurrentStep((prev) => prev + 1);
    },
    onError: (_error: any) => {
      setUploadError('Failed to upload document');
    },
  });

  const onSubmit = () => {
    trigger();
    if (!isValid) {
      return;
    }

    const formData = getValues('projectFinance');

    if (formData.selectedFiles?.length) {
      const formDataSelectedFiles = formData?.selectedFiles.map((f: File) => f.name);
      const formContextDataUploadedFiles = formContextData?.projectFinance?.financeDocs.map((f: File) => f.name);

      const fileUploadCompare = compareTwoArrays(formDataSelectedFiles, formContextDataUploadedFiles);

      if (fileUploadCompare.array1Diff.length > 0) {
        const signAssetPayload = formData.selectedFiles
          .filter((f: File) => fileUploadCompare.array1Diff.includes(f.name))
          .map((file: File) => ({
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            formId: String(params.applicationFormId),
          }));
        uploadFile.mutate({
          signAssetPayload,
          files: formData.selectedFiles,
        });
      } else {
        setCurrentStep((prev) => prev + 1);
      }
    } else {
      updateData({
        projectFinance: { ...formData },
      });
      setCurrentStep((prev) => prev + 1);
    }
  };

  return (
    <div className='p-6 space-y-6 sm:rounded-r-md sm:overflow-hidden'>
      <div>
        <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>Project Financial</h3>
      </div>
      <div className='grid grid-cols-6 gap-6'>
        <h3 className='col-span-6'>Sources</h3>
        <div className='col-span-3'>
          {fields.map((sources, idx) => (
            <div key={sources.id} className='flex items-center justify-between gap-4 my-4'>
              <div className='grid grid-cols-2 gap-5 w-full'>
                <HookInput
                  customClassName='col-span-1'
                  label='Line Item'
                  name={`projectFinance.sources[${idx}].name` as const}
                  id='name'
                  type='text'
                  disabled={
                    getValues('projectFinance.sources')[idx].name === 'City of Birmingham' ||
                    getValues('projectFinance.sources')[idx].name === 'Developer Equity'
                  }
                />
                <HookMaskedInput
                  customClassName='col-span-1'
                  label='Amount ($)'
                  name={`projectFinance.sources[${idx}].amount` as const}
                  id='amount'
                  maskedComponent='currency'
                />
              </div>
              {getValues('projectFinance.sources')[idx].name !== 'City of Birmingham' &&
                getValues('projectFinance.sources')[idx].name !== 'Developer Equity' && (
                  <div
                    className='h-full border p-2 mt-6 rounded-md text-neutralColor-normal bg-gray-50 hover:bg-tertiaryColor-lightest hover:text-tertiaryColor-normal cursor-pointer transition duration-100 ease-in-out'
                    onClick={() => remove(idx)}
                  >
                    <TrashIcon className='h-5 w-6' />
                  </div>
                )}
            </div>
          ))}
          <div className='mt-8'>
            <Button label='Add Source' btnStyle='btn-primary' btnSize='btn-sm' type='button' onClick={addNewSource} />
          </div>
          <p className='mt-5 font-bold text-primaryColor-normal'>
            Total Sources: <CurrencyFormatDisplay value={budgetTotal} prefix='$ ' />
          </p>
        </div>
        <h3 className='col-span-6'>Uses</h3>
        <div className='col-span-2 flex flex-col space-y-3'>
          <div className='flex justify-between items-center'>
            <span>Acquisition Cost</span>
            <HookMaskedInput customClassName='' name='projectFinance.uses.acquisition' id='acquisition' maskedComponent='currency' />
          </div>
          <div className='flex justify-between items-center'>
            <span>Hard Cost</span>
            <HookMaskedInput name='projectFinance.uses.hardCost' id='hard-cost' maskedComponent='currency' />
          </div>
          <div className='flex justify-between items-center'>
            <span>Soft Cost</span>
            <HookMaskedInput name='projectFinance.uses.softCost' id='soft-cost' maskedComponent='currency' />
          </div>
          <div className='flex justify-between items-center'>
            <span>Finance Cost</span>
            <HookMaskedInput name='projectFinance.uses.financeCost' id='finance-cost' maskedComponent='currency' />
          </div>
          <div className='flex justify-between items-center'>
            <span>Developer Cost</span>
            <HookMaskedInput name='projectFinance.uses.developerFeeAmount' id='finance-cost-amount' maskedComponent='currency' />
          </div>
          <div className='flex justify-between items-center'>
            <p className='mt-5 font-bold text-primaryColor-normal'>
              Total Uses: <CurrencyFormatDisplay value={formattedTotalUses} prefix='$ ' />
            </p>
          </div>
        </div>
        {errors && <p className='col-span-6 text-red-600 text-sm mt-1'>{errors?.projectFinance?.message as string}</p>}
        <div className='col-span-6'>
          <HookFileInput
            name='projectFinance.selectedFiles'
            label='Additional Docs'
            id='selectedFiles'
            placeholder='Please upload your project proforma'
            multiple
          />
        </div>
        {uploadError && <p className='text-tertiaryColor-normal text-md col-span-6'>{uploadError}</p>}
        <HookRadioGroup
          className='col-span-6 md:col-span-6'
          label='Will you be using HOME  funds (www.home.com)?'
          name='projectFinance.willUseHomeFunds'
          id='will-use-home-funds'
          options={[
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' },
          ]}
        />
        <HookRadioGroup
          className='col-span-6 md:col-span-6'
          label='Will you be using CDBG funds(www.cdbg.com)?'
          name='projectFinance.willUseCDBGFunds'
          id='will-use-cdbg-funds'
          options={[
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' },
          ]}
        />
      </div>
      <div className='flex gap-4 justify-end mt-5'>
        <Button
          label='Previous'
          disabled={uploadFile.isLoading}
          btnStyle='btn-neutral'
          type='button'
          onClick={() => setCurrentStep((prev) => prev - 1)}
        />
        <Button
          label={uploadFile.isLoading ? 'Uploading Files...' : 'Next'}
          btnStyle={uploadFile?.isLoading ? 'neutral' : 'primary'}
          disabled={uploadFile.isLoading}
          type='button'
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}
